import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { FlexModule } from '@ngbracket/ngx-layout';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { isDevMode, NgModule, provideAppInitializer } from '@angular/core';
import { AkitaNgDevtools } from '@datorama/akita-ngdevtools';
import { environment } from '../environments/environment';
import { TranslocoRootModule } from './transloco-root.module';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { LayoutModule } from '@railmybox/layout';
import localeDe from '@angular/common/locales/de';
import { registerLocaleData } from '@angular/common';
import { AuthInterceptor, NoCacheInterceptor, RefreshTokenInterceptor, SessionInterceptor } from './interceptors';
import { Configuration, ConfigurationParameters } from '@railmybox/api-user';
import { AppRoutingModule } from './app-routing.module';
import { CookiebotConfig } from './cookiebot.config';
import { ngxCookiebotProvider } from '@halloverden/ngx-cookiebot';
import { ToastModule } from 'primeng/toast';
import { ErrorInterceptor } from './interceptors/error.interceptor';
import { MessageService } from 'primeng/api';
import { ServiceWorkerModule } from '@angular/service-worker';
import { UiModule } from '@eg/ui';
import { ComponentsComponent } from './components/components.component';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { RmbTheme } from '@railmybox/shared/util';
import { LoggerModule } from 'ngx-logger';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';

registerLocaleData(localeDe);

if (environment.enableCookieBanner) {
  ngxCookiebotProvider(CookiebotConfig);
}

export function appendGoogleMapsFactory() {
  return () => {
    return new Promise<void>((resolve) => {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${environment.googleMapsApiKey}&libraries=places&language=en`;
      script.addEventListener('load', () => {
        resolve();
      });
      document.body.appendChild(script);
    });
  };
}

export function apiConfigFactory(): Configuration {
  const params: ConfigurationParameters = {
    credentials: {
      main_auth: 'xxx',
    },
  };
  return new Configuration(params);
}

@NgModule({
  declarations: [AppComponent, ComponentsComponent],
  imports: [
    environment.production ? [] : AkitaNgDevtools.forRoot(),
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    TranslocoRootModule,
    ReactiveFormsModule,
    ToastModule,
    FlexModule,
    LayoutModule,
    UiModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    LoggerModule.forRoot({
      colorScheme: ['purple', 'teal', 'gray', 'gray', 'orange', 'red', 'red'],
      level: environment.logLevel,
      serverLogLevel: environment.serverLogLevel,
      disableConsoleLogging: false,
    }),
  ],
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    provideAppInitializer(() => {
      const initializerFn = appendGoogleMapsFactory();
      return initializerFn();
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RefreshTokenInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SessionInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NoCacheInterceptor,
      multi: true,
    },
    {
      provide: Configuration,
      useFactory: apiConfigFactory,
    },
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: RmbTheme,
        options: {
          darkModeSelector: false,
        },
      },
    }),
    {
      // Angular CDK defaults overlays (modals, dropdowns, etc.) to the native
      // Popover API, which renders them in the browser's top layer - a rendering
      // tier that sits above all normal DOM content regardless of z-index. That
      // breaks third-party widgets appended outside Angular's control (e.g.
      // Google's Places Autocomplete .pac-container), which can never render
      // above a CDK overlay's top-layer content no matter their z-index. This
      // restores the pre-CDK-21 stacking behavior for all overlays in the app.
      provide: OVERLAY_DEFAULT_CONFIG,
      useValue: { usePopover: false },
    },
    MessageService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
